import { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import { useCarConfigDispatch, useCarConfigSelector } from "../redux/store";
import { getPostal } from "../../../../common-deprecated/utils/postalUtils";
import { updateFinanceQuote } from "../redux/slices/CarConfigFinanceSlice";
import { getExternalConfigurationObject } from "../utils/typedUtils";
import { setFinanceOption } from "../../../../common-deprecated/redux/actions/UtilActions";
import { trackOrderOpenFinanceCalculator } from "../utils/tracking";
import { POSTAL_CHANNEL_FQS, POSTAL_TOPIC_FQS_QUOTE_SET } from "../../../../common-deprecated/constants";
import { clearModal, updateModalSettings } from "../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_FINANCE_CALCULATOR } from "../utils/modalConstants";
import type { FinanceOptionType } from "../../../../common-deprecated/types/CommonTypes";
import { EnvironmentEnum } from "../../../../common-deprecated/utils";
import { useDebouncedEffect } from "../../../../common-deprecated/hooks";
// The import rule is disabled as using type-only imports for both default + named bindings is not supported in Typescript (TS1363)
// eslint-disable-next-line import/no-duplicates
import type useT1ModalInjection from "../../../../common-deprecated/hooks/useT1ModalInjection";
// eslint-disable-next-line import/no-duplicates
import type { T1InjectionType } from "../../../../common-deprecated/hooks/useT1ModalInjection";
import type { BssCtaTriggeredEvent } from "../utils/types/FinanceEventType";
import { useCarConfigLabel } from "../utils/labelUtils";
import { addNotification } from "../../../../common-deprecated/redux/actions/NotificationActions";

const useAphFinanceCalculatorButton = (
    triggerOrigin?: "cc-cta" | "cc-navigation-top",
): {
    showCalculator: () => void;
    isT1InjectionReady: boolean;
    onClose: (financeOption?: FinanceOptionType) => void;
    containerId: string;
    embedContent: string | null;
    setEmbedContent: (embedContent: string | null) => void;
} => {
    // ----------------------------------------------------------------------
    // Redux data
    // ----------------------------------------------------------------------
    const dispatch = useCarConfigDispatch();
    const t1InjectionComponents = useRef<T1InjectionType>();
    const [isT1InjectionReady, setIsT1InjectionReady] = useState<boolean>(false);
    const [financeQuote, setFinanceQuote] = useState<string | undefined>();
    const [financeProduct, setFinanceProduct] = useState<string | undefined>();
    const [pendingClose, setPendingClose] = useState(false);
    const [embedContent, setEmbedContent] = useState<string | null>(null);

    const financeGatewayUrl = useCarConfigSelector((state) => state.carConfigSettings.financeCalculatorUrl);
    const urlWebsiteInjection = useCarConfigSelector((state) => state.commonSettings.urlWebsiteInjection);
    const environment = useCarConfigSelector((state) => state.commonSettings.environment);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const ccSaveConfig = useCarConfigSelector(getExternalConfigurationObject, shallowEqual);

    const notificationLabel = useCarConfigLabel("carConfigA11yFinanceModalOpened");
    const HIDE_MODAL_EXT_EVENT = "hide-build-amd-modal";
    const IFRAME_CTA_EVENT = "bss_cta_triggered";

    const fetchFinanceConfig = useCallback(async () => {
        const fallbackGateway = `${carConfigSettings.loadSaveServer}/config/find/json/finance`;
        const ccSaveResponse = await fetch(fallbackGateway, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(ccSaveConfig),
            mode: "cors",
        });

        const response = await ccSaveResponse.json();

        // Opening the calculator will look at the ccSaveResponse object, ProductType is not added by default.
        // This is required to have the calculator open on the right tab.
        if (ccSaveConfig.ProductType && !response.ProductType) {
            response.ProductType = ccSaveConfig.ProductType;
        }

        if (carConfigSettings.financeCalculatorEnvironment) {
            response.Environment = carConfigSettings.financeCalculatorEnvironment;
        } else if (environment === EnvironmentEnum.Preview) {
            response.Environment = "test";
        }

        return response;
    }, [ccSaveConfig]);

    // ----------------------------------------------------------------------
    // Injection logic
    // ----------------------------------------------------------------------
    const postalSubscription = useRef<ReturnType<Postal["subscribe"]>>();

    const injectionProps: Parameters<typeof useT1ModalInjection>[0] = {
        urlWebsite: urlWebsiteInjection,
        containerId: "cc-injection-finance-amd",
        contentUrl: financeGatewayUrl,
    };

    // non-TFS countries use a different way of injecting the finance calculator
    const useNonTFSInjection = injectionProps.contentUrl.includes("nontfs");

    // Load T1 injection logic, listen to financeQuoteId updates.
    useEffect(() => {
        // TODO Remove fqs requirement once makolab has updated their light finance calculator.
        window.require([`${urlWebsiteInjection}/scripts/T1-injection-components.js`], (components) => {
            t1InjectionComponents.current = components;
        });

        setIsT1InjectionReady(true);
        // Should only run once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // As these values can change on multiple occasions, we use a debouncedEffect so the actual quoteConfig only gets set once.
    useDebouncedEffect(
        () => {
            // Updating the finance quote will automatically start a finance fetch
            if (financeQuote || financeProduct) {
                dispatch(updateFinanceQuote({ finance: financeQuote ?? null, productName: financeProduct }));
            }

            if (pendingClose) onClose();
        },
        100,
        [financeQuote, financeProduct, pendingClose],
    );

    const onCloseWrapper = (): void => setPendingClose(true);

    const onClose = (financeOption?: FinanceOptionType): void => {
        if (postalSubscription.current) getPostal().unsubscribe(postalSubscription.current);
        // This event is needed. Cleans up code on makolab side. If this is not present the finance calculator will crash when opening for the second time
        document.dispatchEvent(new CustomEvent("bss-destroy-connector-event"));
        dispatch(setFinanceOption(financeOption ?? "monthly"));

        // Clear the finance modal otherwise the injection will cause trouble the second time opening the modal
        dispatch(clearModal(MODAL_FINANCE_CALCULATOR));

        document.removeEventListener(HIDE_MODAL_EXT_EVENT, onCloseWrapper);
        document.removeEventListener(IFRAME_CTA_EVENT, onModalCtaClick);
    };

    const onModalCtaClick = (event: Event): void => {
        const eventData = (event as BssCtaTriggeredEvent).detail?.params || null;
        if (!eventData) return;
        // Open custom modal content for iframe
        if (eventData && eventData.url) {
            setEmbedContent(eventData.url);
        }
    };

    const showCalculator = async (): Promise<void> => {
        const financeConfig = await fetchFinanceConfig();

        trackOrderOpenFinanceCalculator(triggerOrigin);

        // Wait for the injection script to fully initialize
        window.requestAnimationFrame(() => {
            window.require(
                injectionProps.contentUrl,
                (module) => {
                    const renderObject = {
                        target: useNonTFSInjection
                            ? { 0: document.querySelector(`#${injectionProps.containerId}`) }
                            : document.querySelector(`#${injectionProps.containerId}`),
                        configuration: financeConfig,
                        isAppendedToBody: false,
                        module,
                        ...(useNonTFSInjection ? { url: injectionProps.contentUrl } : {}),
                    };

                    module.render(renderObject);
                },
                // Error callback: doesn't return anything, though is triggered when something goes wrong.
                (_) => {
                    dispatch(
                        updateModalSettings(
                            { injectionLoading: false, injectionError: true },
                            MODAL_FINANCE_CALCULATOR,
                        ),
                    );
                },
            );
        });

        // Same events as defined in the FinanceQuoteService
        postalSubscription.current = getPostal().subscribe({
            channel: POSTAL_CHANNEL_FQS,
            topic: POSTAL_TOPIC_FQS_QUOTE_SET,
            callback: (data: { finance: string; productType: string }) => {
                if (data.finance) setFinanceQuote(data.finance);
                if (data.productType) setFinanceProduct(data.productType);
            },
        });

        // setTimeout(() => {
        //     // TODO: REMOVE THIS WHEN MAKOLAB HAS DELIVERED AND THIS IS TESTED
        //     // To test embed iframe
        //     onModalCtaClick(
        //         new CustomEvent(IFRAME_CTA_EVENT, {
        //             detail: {
        //                 url: "https://formulieren.toyota.nl/offerte-aanvraag.html?amount=35995&duration=60&deposit=12599&finalterm=8891&productCode=50007&cid=LHE4y5Y2s",
        //             },
        //         }),
        //     );
        // }, 5000);

        document.addEventListener(HIDE_MODAL_EXT_EVENT, onCloseWrapper);
        dispatch(
            addNotification([
                {
                    title: notificationLabel,
                    srOnly: true,
                },
            ]),
        );
        document.addEventListener(IFRAME_CTA_EVENT, onModalCtaClick);
    };

    return {
        showCalculator,
        isT1InjectionReady,
        onClose,
        containerId: injectionProps.containerId,
        embedContent,
        setEmbedContent,
    };
};

export default useAphFinanceCalculatorButton;
